'use strict';

import { getLoggedServingsOfRecipe } from '@utils/Meals';

/**
 * Generate yield warnings from pre-computed dish servings data.
 * This function uses the same data source as the modal header yield display,
 * ensuring consistency between the yield shown and the yield notes.
 *
 * @param {Array} dishServings - Array from getTotalServings(): {uuid, title, totalYield, totalServings, totalLeftoverServings, scaling}
 * @param {number} extraServings - Extra servings (batch cooking)
 * @returns {Array<string>} Array of warning messages
 */
export function generateWarningsFromDishServings(dishServings, extraServings = 0) {
    const warnings = [];

    dishServings.forEach(({title, totalYield, totalServings}) => {
        const totalNeeded = totalServings + extraServings;

        if (totalYield > totalNeeded) {
            warnings.push(`${title}: Scaled to ${totalYield} ${totalYield > 1 ? "servings" : "serving"} (${totalNeeded} ${totalNeeded > 1 ? "servings" : "serving"} needed)`);
        }
    });

    return warnings;
}

/**
 * Calculate yield warnings from raw dish/participant data (participant-only, no leftovers).
 * Used by EditMealServingsModalSecondary (scaling mode) where leftovers don't apply.
 *
 * @see generateWarningsFromDishServings - For the Primary modal (includes leftovers)
 */
export function calculateYieldWarnings(dishes, contents, participants, extraServings = 0) {
    const warnings = [];

    dishes.forEach(({main: dish}) => {
        const content = contents[dish.recipe_uuid || dish.food_uuid];
        if (!content) return;

        const servings = content.servings || 1;
        const totalServings = participants.reduce((sum, participant) => sum + (
            participant.uuid == 'USER' ? getLoggedServingsOfRecipe(dish, content) : participant.portion), 0);
        const scaling = totalServings > 0 ? Math.ceil((totalServings + extraServings) / servings) : (1 + extraServings);
        const totalYield = scaling * servings;
        const totalNeeded = totalServings + extraServings;

        if (totalYield > totalNeeded) {
            const dishName = contents[dish.recipe_uuid]?.title || contents[dish.food_uuid]?.name;
            warnings.push(`${dishName}: Scaled to ${totalYield} ${totalYield > 1 ? "servings" : "serving"} (${totalNeeded} ${totalNeeded > 1 ? "servings" : "serving"} needed)`);
        }
    });

    return warnings;
}
